import { useId, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { MEDIA_GENERATION_CAP_MAX } from '@mg/shared';
import type { Tenant } from '@mg/shared';
import { api, ApiRequestError } from '../../lib/api';
import { useTenant } from '../../auth/TenantContext';
import { useIsWorkspaceAdmin } from '../../auth/useWorkspaceAdmin';
import { Card, CardBody, CardHeader } from '../ui/Card';
import { Button } from '../ui/Button';
import { FormField, Input } from '../ui/Field';
import { useFooterSave } from '../settings/footerSave';

/**
 * The workspace's image-generation limit.
 *
 * Per workspace rather than install-wide because workspaces differ wildly in how much they
 * generate, and because Smorgas Social · FLUX runs on OUR bill — a modal built for iteration can multiply
 * spend quickly. Empty = uncapped, which is the default and what every existing workspace has.
 *
 * The card shows CURRENT SPEND next to the field. Setting a ceiling without knowing what normal
 * looks like is guesswork, and the first thing you learn from a bad guess is a refusal.
 *
 * On the Brand Workspace v2 Workflow page (inside a FooterSaveProvider) the limit is saved by the
 * page's sticky Save footer, after the approval settings (`order` 20), and "Save limit" is hidden.
 * Everywhere else the registration does nothing. `canEdit` is the v2 page's rule (by the workspace
 * being edited, E20); without it, today's useIsWorkspaceAdmin.
 */
export function WorkspaceMediaSection({
  workspace,
  canEdit: canEditProp,
}: {
  workspace: Tenant;
  /** Who may change the limit. Default: today's rule, useIsWorkspaceAdmin. */
  canEdit?: boolean;
}) {
  const { refresh } = useTenant();
  const isWorkspaceAdmin = useIsWorkspaceAdmin();
  const canEdit = canEditProp ?? isWorkspaceAdmin;
  const capId = useId();

  // Empty string IS a value here — it means uncapped — so the field is a string, not a number.
  const savedCap = workspace.mediaGenerationDailyCap != null ? String(workspace.mediaGenerationDailyCap) : '';
  const [cap, setCap] = useState<string>(savedCap);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const usage = useQuery({
    queryKey: ['media-generation-usage', workspace.id],
    queryFn: ({ signal }) => api.getGenerationUsage(signal),
  });

  const trimmed = cap.trim();
  const parsed = trimmed === '' ? null : Number(trimmed);
  const invalid =
    parsed != null &&
    (!Number.isInteger(parsed) || parsed < 1 || parsed > MEDIA_GENERATION_CAP_MAX);
  const dirty = parsed !== (workspace.mediaGenerationDailyCap ?? null);

  const save = useMutation({
    mutationFn: () => api.updateTenant(workspace.id, { mediaGenerationDailyCap: parsed }),
    onSuccess: async () => {
      setError(null);
      setSaved(true);
      await refresh();
      await usage.refetch();
    },
    onError: (err) =>
      setError(err instanceof ApiRequestError ? err.message : 'Could not save the limit.'),
  });

  const { inFooter } = useFooterSave({
    id: 'image-generation',
    order: 20,
    dirty,
    canSave: !invalid,
    saving: save.isPending,
    // The mutation's own onError shows why a save failed; the footer only needs the outcome.
    save: () => save.mutateAsync().then(
      () => true,
      () => false,
    ),
    discard: () => {
      setCap(savedCap);
      setSaved(false);
      setError(null);
    },
  });

  const used = usage.data?.used;
  const liveCap = usage.data?.cap ?? null;

  return (
    <Card>
      <CardHeader
        title="Image generation"
        description="How many images this workspace may generate, and how many it has generated recently."
      />
      <CardBody className="space-y-4">
        <FormField
          label="Daily limit"
          htmlFor={capId}
          hint={`Image generations allowed per rolling 24 hours. Leave empty for no limit. Every attempt counts, including ones that fail — a failed generation still costs a provider call. Maximum ${MEDIA_GENERATION_CAP_MAX}.`}
        >
          <div className="flex items-center gap-3">
            <Input
              id={capId}
              type="number"
              min={1}
              max={MEDIA_GENERATION_CAP_MAX}
              step={1}
              inputMode="numeric"
              className="w-40"
              placeholder="No limit"
              value={cap}
              disabled={!canEdit || save.isPending}
              onChange={(e) => {
                setSaved(false);
                setCap(e.target.value);
              }}
            />
            <span className="text-sm text-ink-muted">
              {usage.isLoading
                ? 'Loading usage…'
                : used == null
                  ? ''
                  : liveCap == null
                    ? `${used} generated in the last 24 hours (currently uncapped).`
                    : `${used} of ${liveCap} used in the last 24 hours.`}
            </span>
          </div>
        </FormField>

        {invalid && (
          <p className="text-sm text-red-600 dark:text-error-dark">
            Enter a whole number between 1 and {MEDIA_GENERATION_CAP_MAX}, or leave it empty for no
            limit.
          </p>
        )}
        {/* A cap below current spend is legal and immediate — say so rather than letting the next
            generation fail with no explanation. */}
        {!invalid && parsed != null && used != null && used >= parsed && (
          <p className="text-sm text-amber-700 dark:text-warning-dark">
            This workspace has already generated {used} image{used === 1 ? '' : 's'} in the last 24
            hours, so this limit takes effect immediately — the next generation will be refused
            until the oldest ones fall outside the window.
          </p>
        )}
        {error && <p className="text-sm text-red-600 dark:text-error-dark">{error}</p>}
        {saved && !dirty && <p className="text-sm text-success dark:text-success-dark">Saved.</p>}

        {canEdit && !inFooter && (
          <div className="flex justify-end">
            <Button
              loading={save.isPending}
              disabled={!dirty || invalid}
              onClick={() => save.mutate()}
            >
              Save limit
            </Button>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
